/**
 * Centralized query keys factory for type-safe query key management.
 * Each entity has its own namespace with standardized query key patterns.
 *
 * Queries are defined by `queryOptions` factories that consume these keys —
 * app-global reads in `query/query-options.ts`, feature reads in
 * `modules/<feature>/query/query-options.ts`, route-scoped reads in the
 * route's `-query/query-options.ts`.
 *
 * @example
 * ```typescript
 * // Basic definition
 * const makeQueryKeys = {
 *   users: {
 *     all: () => ['users'] as const,
 *     index: (filters?: Record<string, unknown>) => ['users', 'index', filters] as const,
 *     show: (id: string) => ['users', id] as const
 *   }
 * } as const
 *
 * // Query — factory built on these keys
 * const userQuery = useQuery(userQueryOptions('123'))
 *
 * // Query with filters
 * const filteredUsers = useQuery(
 *   usersListQueryOptions({ status: 'active', role: 'admin' })
 * )
 *
 * // Mutation — manual mutationFn, body typed from Api*Data['body']
 * const updateUser = useMutation({
 *   mutationFn: (body: ApiUsersUpdateMeData['body']) =>
 *     apiUsersUpdateMe({ body }),
 *   onSuccess: () =>
 *     queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 * })
 *
 * // Invalidate all user queries (array prefix matching)
 * queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 *
 * // Invalidate one filtered list
 * queryClient.invalidateQueries({
 *   queryKey: makeQueryKeys.users.index({ status: 'active' })
 * })
 *
 * // Prefetch / SSR ensure
 * queryClient.ensureQueryData(userQueryOptions('123'))
 * ```
 */
export const makeQueryKeys = {
	auth: {
		session: () => ['auth', 'session'] as const
	},
	appConfig: {
		// The instance branding: the same document the Keycloak login page
		// reads, drawn here as the console's name and marks.
		loginBranding: () => ['app-config', 'login-branding'] as const
	},
	// `all` alone, and it is a prefix rather than a leftover: the ban, unban
	// and delete dialogs invalidate it, and whichever people list mounts them
	// keys under it. The detail family — sessions, push registrations,
	// notification preferences — left with the cross-tenant user detail in
	// OPSSTD-5305; per-person inspection belongs to the org admin (MS9).
	users: {
		all: () => ['users'] as const
	},
	exports: {
		detail: (id: string) => ['exports', 'detail', id] as const
	},
	// `index` is the console's parc of customers, one page of it per key:
	// `list-organizations` is cursor-paginated, so the query that defines the
	// page — limit, filters, sort — is what identifies the read, and the
	// cursor itself is the infinite query's page param rather than part of the
	// key.
	//
	// `detail` stays beside it and is a different question: `get-organization`
	// addresses one id and answers with the full organization, including the
	// three failure fields and the attempt counter the list item does not
	// carry. One id per key, because the detail page is its only consumer —
	// OPSSTD-5427 took the list's multi-id read away, and "why did this
	// creation stop" is a question the detail page answers now.
	//
	// `slugAvailability` sits under the same parent so that creating one
	// organization invalidates all three: the new row belongs in the list, and
	// the slug it took is no longer free.
	organizations: {
		all: () => ['organizations'] as const,
		index: (body: Record<string, unknown>) =>
			['organizations', 'index', body] as const,
		detail: (id: string) => ['organizations', 'detail', id] as const,
		// The first administrator's invitation, keyed apart from `detail`
		// because it is a second call and not a field of the first — see
		// `get-first-admin-invitation`, which stays off `Organization` so the
		// list the console polls does not pay for it. Under the same parent so
		// that a resend, which moves only this, can invalidate only this.
		firstAdminInvitation: (id: string) =>
			['organizations', 'first-admin-invitation', id] as const,
		// The organization's people, as one cursored listing.
		//
		// **The cursor is not in the key.** It is the infinite query's page
		// param, so the key identifies the *listing* — the organization, its
		// filters — and every page of it lives under one entry. A filter the
		// operator changes therefore starts a different query rather than
		// carrying a cursor across a shape the server no longer recognises,
		// which is the case the endpoint refuses with `cursor_invalid`.
		//
		// Under the same parent as `detail`, so inviting an owner or
		// conferring ownership can invalidate the people of that
		// organization and nothing else.
		people: (id: string, filters?: Record<string, unknown>) =>
			['organizations', 'people', id, filters ?? {}] as const,
		slugAvailability: (slug: string) =>
			['organizations', 'slug-availability', slug] as const
	},
	// The instance itself, which belongs to no organization. Kept in its own
	// namespace for that reason: invalidating an organization must not touch
	// a reading about the machine every organization runs on.
	instance: {
		all: () => ['instance'] as const,
		health: () => ['instance', 'health'] as const
	},
	// The operator's own audit log — what the console itself did, across
	// every organization at once — one entry per listing exactly as
	// `organizations.index` is: `list-audit-events` is cursor-paginated, so
	// the query that defines the page (limit, filters) identifies the read
	// and the cursor stays the infinite query's page param rather than part
	// of the key.
	audit: {
		all: () => ['audit'] as const,
		index: (body: Record<string, unknown>) =>
			['audit', 'index', body] as const
	},
	// The console's own operators. `list-operators` is not paginated — the
	// set is a handful of people — so the filters alone identify the read.
	// Every action on an operator invalidates `all`.
	operators: {
		all: () => ['operators'] as const,
		index: (query: Record<string, unknown>) =>
			['operators', 'index', query] as const
	},
	// The verdict on one operator invitation code, read by the signed-out
	// activation page. Apart from `operators` because it is asked without a
	// session and answers about a code, not about the list.
	operatorInvitation: {
		validate: (code: string) =>
			['operator-invitation', 'validate', code] as const
	}
} as const
